import React from 'react';
import {
  Box,
  Text,
  SimpleGrid,
  Flex,
  Icon,
  Badge,
  List,
  ListItem,
  ListIcon,
  CircularProgress,
  CircularProgressLabel,
  useColorModeValue,
  Divider,
  VStack,
  HStack,
  IconButton,
  Tooltip
} from '@chakra-ui/react';
import { FiCheckCircle, FiAlertTriangle, FiXCircle, FiTrendingUp, FiFeather, FiSearch, FiShield, FiEyeOff, FiRefreshCw } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

interface AuditIssue {
    id: string;
    message: string;
    severity: 'critical' | 'warning' | 'info';
    ignored?: boolean;
}

interface AuditSection {
    score: number;
    issues?: AuditIssue[];
    strengths?: string[];
    weaknesses?: string[];
    missingKeywords?: string[];
}

export interface BulletAuditResult {
    overallScore: number;
    auditor: string;
    analysis: {
        compliance?: AuditSection;
        copywriting?: AuditSection;
        seo?: AuditSection;
    };
    suggestions?: string[];
}

interface BulletAuditReportProps {
    audit: BulletAuditResult | null;
    isLoading: boolean;
    onIgnoreRule?: (issueId: string, action: 'ignore' | 'restore') => void;
}

const ScoreCard = ({ title, score, icon, children, colorScheme = "teal" }: any) => {
    // Hooks must be unconditional
    const bg = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    
    let color = "red.400";
    if (score >= 50) color = "orange.400";
    if (score >= 75) color = "green.400";

    return (
        <Box 
            bg={bg} 
            p={5} 
            borderRadius="xl" 
            border="1px solid" 
            borderColor={borderColor}
            boxShadow="lg"
            position="relative"
            overflow="hidden"
        >
            <Flex justify="space-between" align="start" mb={4}>
                <HStack>
                    <Icon as={icon} boxSize={5} color={`${colorScheme}.400`} />
                    <Text fontWeight="bold" fontSize="lg">{title}</Text>
                </HStack>
                <CircularProgress value={score} color={color} size="50px" thickness="10px">
                    <CircularProgressLabel fontSize="xs" fontWeight="bold">{score}</CircularProgressLabel>
                </CircularProgress>
            </Flex>
            <Box fontSize="sm">
                {children}
            </Box>
        </Box>
    );
};

const BulletAuditReport: React.FC<BulletAuditReportProps> = ({ audit, isLoading, onIgnoreRule }) => {
    const { t } = useTranslation();
    const suggestionsBg = useColorModeValue("blue.50", "whiteAlpha.100");

    if (isLoading) {
        return (
            <Box mt={8} textAlign="center" py={10}>
                <CircularProgress isIndeterminate color="teal.400" />
                <Text mt={4} color="gray.500">Audit en cours par {audit?.auditor || "l'IA"}...</Text>
            </Box>
        );
    }

    if (!audit) return null;

    return (
        <Box mt={10} as="section">
            <Flex align="center" mb={6} gap={3}>
                <Text fontSize="2xl" fontWeight="bold" bgGradient="linear(to-r, purple.400, pink.400)" bgClip="text">
                    Rapport d'Audit Avancé
                </Text>
                <Badge colorScheme="purple" variant="subtle" fontSize="0.9em" borderRadius="full" px={3}>
                    {audit.auditor}
                </Badge>
                {audit.overallScore && (
                    <Badge 
                        colorScheme={audit.overallScore >= 75 ? "green" : audit.overallScore >= 50 ? "orange" : "red"} 
                        fontSize="1em" 
                        borderRadius="md" 
                        px={2}
                    >
                        Global: {audit.overallScore}/100
                    </Badge>
                )}
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                
                {/* Compliance */}
                {audit.analysis && audit.analysis.compliance && (
                    <ScoreCard 
                        title="Conformité" 
                        score={audit.analysis.compliance.score} 
                        icon={FiShield}
                        colorScheme="blue"
                    >
                        <List spacing={2}>
                            {audit.analysis.compliance.issues?.length === 0 && (
                                <ListItem display="flex" alignItems="center" color="green.400">
                                    <ListIcon as={FiCheckCircle} />
                                    Aucune violation détectée
                                </ListItem>
                            )}
                            {audit.analysis.compliance.issues?.map((issue, i) => (
                                <ListItem key={i} display="flex" alignItems="start" color={issue.ignored ? 'gray.500' : (issue.severity === 'critical' ? 'red.400' : 'orange.300')}>
                                    <ListIcon as={issue.ignored ? FiCheckCircle : (issue.severity === 'critical' ? FiXCircle : FiAlertTriangle)} mt={1} />
                                    <Flex direction="column" flex="1">
                                        <Text fontSize="xs" as={issue.ignored ? "s" : undefined}>{issue.message}</Text>
                                    </Flex>
                                    {onIgnoreRule && issue.id && (
                                        <Tooltip label={issue.ignored ? "Restaurer la règle" : "Ignorer la règle"}>
                                            <IconButton 
                                                aria-label="Toggle rule" 
                                                icon={issue.ignored ? <FiRefreshCw /> : <FiEyeOff />} 
                                                size="xs" 
                                                variant="ghost" 
                                                opacity={0.5}
                                                _hover={{ opacity: 1 }}
                                                onClick={() => onIgnoreRule(issue.id, issue.ignored ? 'restore' : 'ignore')}
                                            />
                                        </Tooltip>
                                    )}
                                </ListItem>
                            ))}
                        </List>
                    </ScoreCard>
                )}

                {/* Copywriting */}
                {audit.analysis && audit.analysis.copywriting && (
                    <ScoreCard 
                        title="Copywriting" 
                        score={audit.analysis.copywriting.score} 
                        icon={FiFeather}
                        colorScheme="pink"
                    >
                        <VStack align="stretch" spacing={2}>
                            {audit.analysis.copywriting.strengths?.slice(0, 2).map((s, i) => (
                                <HStack key={`s-${i}`} color="green.400">
                                    <Icon as={FiTrendingUp} boxSize={3} />
                                    <Text fontSize="xs">{s}</Text>
                                </HStack>
                            ))}
                             {audit.analysis.copywriting.weaknesses?.slice(0, 2).map((w, i) => (
                                <HStack key={`w-${i}`} color="red.300">
                                    <Icon as={FiAlertTriangle} boxSize={3} />
                                    <Text fontSize="xs">{w}</Text>
                                </HStack>
                            ))}
                        </VStack>
                    </ScoreCard>
                )}

                {/* SEO */}
                {audit.analysis && audit.analysis.seo && (
                    <ScoreCard 
                        title="SEO" 
                        score={audit.analysis.seo.score} 
                        icon={FiSearch}
                        colorScheme="green"
                    >
                        {audit.analysis.seo.missingKeywords && audit.analysis.seo.missingKeywords.length > 0 ? (
                            <Box>
                                <Text fontSize="xs" color="gray.400" mb={1}>Mots-clés manquants :</Text>
                                <Flex wrap="wrap" gap={1}>
                                    {audit.analysis.seo.missingKeywords.map((kw, i) => (
                                        <Badge key={i} colorScheme="red" variant="outline" fontSize="xs">
                                            {kw}
                                        </Badge>
                                    ))}
                                </Flex>
                            </Box>
                        ) : (
                            <Text color="green.400" fontSize="sm">
                                <Icon as={FiCheckCircle} mr={1} />
                                Tous les mots-clés sont présents
                            </Text>
                        )}
                    </ScoreCard>
                )}
            </SimpleGrid>

            {/* Global Suggestions */}
            {audit.suggestions && audit.suggestions.length > 0 && (
                <Box mt={6} bg={suggestionsBg} p={4} borderRadius="lg">
                    <Text fontWeight="bold" mb={2} color="blue.400">Suggestions d'amélioration :</Text>
                    <List spacing={1}>
                         {audit.suggestions.map((s, i) => (
                             <ListItem key={i} fontSize="sm" display="flex" alignItems="center">
                                 <ListIcon as={FiTrendingUp} color="blue.400" />
                                 {s}
                             </ListItem>
                         ))}
                    </List>
                </Box>
            )}
        </Box>
    );
};

export default BulletAuditReport;
